"use client";

import React, { useState } from "react";
import { Check, ShieldCheck, Clock, Star, Loader as Loader2 } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="7384" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-lg bg-accent flex items-center justify-center mb-2">
          <Check className="size-8 text-accent-foreground" />
        </div>
        <h3 className="text-2xl font-bold text-foreground">Consultation Requested!</h3>
        <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-border hover:bg-muted text-foreground">
          Request Another Time
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Your Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Smith" {...field} className="h-11 border-input bg-card focus-visible:ring-2 focus-visible:ring-ring/20 focus-visible:border-ring transition-all duration-200" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Contact Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="0400 000 000" {...field} className="h-11 border-input bg-card focus-visible:ring-2 focus-visible:ring-ring/20 focus-visible:border-ring transition-all duration-200" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="jane@example.com" {...field} className="h-11 border-input bg-card focus-visible:ring-2 focus-visible:ring-ring/20 focus-visible:border-ring transition-all duration-200" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="preferred_date" rules={{
      required: "Please choose a preferred date"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Preferred Consultation Date</FormLabel>
            <FormControl>
              <Input type="date" {...field} className="h-11 border-input bg-card focus-visible:ring-2 focus-visible:ring-ring/20 focus-visible:border-ring transition-all duration-200" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="consultation_type" rules={{
      required: "Please select a consultation type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Consultation Type</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="h-11 border-input bg-card focus:ring-2 focus:ring-ring/20 focus:border-ring transition-all duration-200">
                  <SelectValue placeholder="Select consultation type" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="rounded-xl border-border shadow-lg">
                {["Video Call", "Phone Call", "In-Person"].map((option, i) => <SelectItem key={i} value={option} data-index={i}>
                    {option}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="additional_info" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Tell me about your needs</FormLabel>
            <FormControl>
              <textarea placeholder="Briefly describe your situation or any specific questions you have for our session..." {...field} className="flex min-h-[100px] w-full border border-input bg-card px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/20 focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 resize-y" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm flex items-center gap-2">
          <span className="font-bold">Error:</span> {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-base shadow-lg active:translate-y-[2px] transition-all duration-150 mt-2">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-5 animate-spin" />
            Scheduling...
          </> : "Schedule My Consultation"}
      </Button>
      
      <p className="text-xs text-center text-muted-foreground mt-4">
        Your information will never be shared or sold. All information shared will be kept strictly confidential.
      </p>
    </Form>;
}
export default function RequestAFreeQuote() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const benefits = [{
    title: "Empathetic Advocacy",
    desc: "Compassionate guidance tailored to your family's unique needs."
  }, {
    title: "Expert Knowledge",
    desc: "Deep understanding of support systems and community resources."
  }, {
    title: "Clear Path Forward",
    desc: "We work together to create actionable steps for your journey."
  }, {
    title: "Confidential Support",
    desc: "A safe space to discuss challenges and find sustainable solutions."
  }];
  return <section id="book-a-consultation" className="relative py-20 overflow-hidden bg-muted text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div ref={sectionRef} className={cn("flex flex-col justify-center space-y-8 transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
            <div className="space-y-4">
              <Badge variant="secondary" className="bg-chart-2/20 text-chart-2 hover:bg-chart-2/30 border-none px-3 py-1 text-xs font-bold uppercase tracking-wider shadow-sm">
                <Clock className="size-3.5 mr-1.5" />
                Compassionate Professional Guidance
              </Badge>
              
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary tracking-tight font-serif leading-tight">
                Your Partner in <br />
                <span className="text-foreground">Family Navigation.</span>
              </h2>
              
              <p className="text-lg text-muted-foreground max-w-lg leading-relaxed font-sans">
                Navigating life's transitions requires more than just information—it requires a partner. Let's sit down and create a clear path forward together.
              </p>
            </div>

            <div className="space-y-6">
              {benefits.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-4 group">
                  <div className="mt-1 flex-shrink-0 size-6 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Check className="size-4 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground text-base">{item.title}</h3>
                    <p className="text-muted-foreground text-sm mt-1">{item.desc}</p>
                  </div>
                </div>)}
            </div>
          </div>

          <div className={cn("relative transition-all duration-700 delay-200 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
            <Card className="border border-border shadow-xl bg-card/80 backdrop-blur-sm overflow-hidden">
              <CardHeader className="bg-card border-b border-border/50 pb-6">
                <CardTitle className="text-2xl font-bold text-foreground font-serif">
                  Book a Consultation
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  Fill out the details below and I'll be in touch to confirm a time that works for you.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 md:p-8 bg-card">
                <FormProvider formKey="consultation_form_patientbooking" formId="consultation_form_patientbooking" sectionName="request-quote-section" defaultValues={{
                name: "",
                email: "",
                phone: "",
                preferred_date: "",
                consultation_type: "",
                additional_info: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true,
                  pattern: "phone"
                },
                preferred_date: {
                  required: true
                },
                consultation_type: {
                  required: true
                }
              }} submitText="Get My Free Quote" successMessage="Thank you! Your quote request has been received. We will contact you shortly.">
                  <QuoteForm />
                </FormProvider>
              </CardContent>
            </Card>
            
            <div className="absolute -top-10 -right-10 size-40 bg-primary/5 rounded-lg blur-3xl -z-10 pointer-events-none" />
            <div className="absolute -bottom-10 -left-10 size-40 bg-chart-2/5 rounded-lg blur-3xl -z-10 pointer-events-none" />
          </div>
        </div>
      </div>
    </section>;
}